import type { CoursePageView } from "~/business-areas/course/course.model";
import type { CourseSessionView } from "~/business-areas/course-session/course-session.model";

const COOKIE_NAME = "registration_token";
const COOKIE_MAX_AGE_IN_SECONDS = 60 * 60 * 24;

/*
The cookie proves the user actually asked to register, so that merely opening a link while
already signed in doesn't enrol anyone. It doesn't survive a magic link opened in another
browser or on another device, so the same intent also travels in the redirect URL.
*/
const INTENT_QUERY_PARAMETER = "register";

export function useRegistrationLinkFlow() {
  const cookie = useCookie<string | null>(COOKIE_NAME, {
    maxAge: COOKIE_MAX_AGE_IN_SECONDS,
  });
  const { me } = useCurrentUser();
  const route = useRoute();
  const router = useRouter();

  function save(registration_token?: string) {
    cookie.value = registration_token ?? null;
  }

  async function erase() {
    cookie.value = null;

    if (route.query[INTENT_QUERY_PARAMETER] === undefined) return;

    const query = { ...route.query };
    delete query[INTENT_QUERY_PARAMETER];

    await router.replace({ query });
  }

  function isActive(registration_token?: string) {
    if (!me.value || !registration_token) return false;

    return (
      registration_token === cookie.value ||
      route.query[INTENT_QUERY_PARAMETER] === "1"
    );
  }

  return { save, erase, isActive };
}

function toAuthenticationPath(fullPath: string) {
  const target = new URL(fullPath, "http://localhost");
  target.searchParams.set(INTENT_QUERY_PARAMETER, "1");

  return `/auth/login?redirect=${encodeURIComponent(`${target.pathname}${target.search}`)}`;
}

export function useRegistrationLink({
  session,
  registration_token,
}: {
  session: MaybeRefOrGetter<CourseSessionView | undefined>;
  registration_token?: MaybeRefOrGetter<string | undefined>;
}) {
  const route = useRoute();
  const flow = useRegistrationLinkFlow();
  const currentUser = useEnrichedCurrentUser();
  const { isRegisteredToCourseSession } = useLearnerProfile();
  const onboarding = useOnboarding();
  const { $analytics } = useNuxtApp();

  const status = ref<"idle" | "pending" | "success" | "error">("idle");

  async function execute() {
    const target = toValue(session);

    if (!target || status.value === "pending") return;

    status.value = "pending";

    try {
      await $fetch(
        `/api/courses/${target.course_id}/sessions/${target.id}/register`,
        {
          method: "POST",
          body: { registration_token: toValue(registration_token) },
        },
      );

      status.value = "success";
      $analytics.track("registration_link_used");

      await flow.erase();
      await currentUser.refresh();

      useDialog("postRegistrationLinkDialog").open({ session: target });
    } catch (error) {
      /*
      A resume can legitimately fire on a session the user already joined (link opened twice,
      two tabs). Re-read the profile rather than matching on the endpoint's error message.
      */
      await currentUser.refresh();

      if (isRegisteredToCourseSession(target.id)) {
        status.value = "success";

        return flow.erase();
      }

      status.value = "error";
      $analytics.track("registration_link_failed");
      useMonitoring().logError(
        error instanceof Error ? error : new Error(String(error)),
      );
      showErrorToastNotification(
        "Ton inscription n'a pas pu être enregistrée. Réessaie ou contacte-nous.",
      );
    }
  }

  async function register() {
    const target = toValue(session);

    if (!target) return;

    flow.save(toValue(registration_token));

    if (!currentUser.me.value)
      return navigateTo(toAuthenticationPath(route.fullPath));

    if (currentUser.me.value.company_name && currentUser.me.value.job_title)
      return execute();

    // Prefill the company from the organization behind the link so onboarding asks for less
    await currentUser.update({
      company_name:
        currentUser.me.value.company_name ?? target.organization?.name,
    });

    return onboarding.start({
      origin: "registration",
      personalInformationOnly: true,
      showToastNotificationOnComplete: false,
      onComplete: () => {
        execute();
      },
    });
  }

  return {
    registrationLinkFlow: flow,
    register,
    status,
  };
}

/*
Runs once per course page. The resume has to survive a full auth round trip, so it watches
its own preconditions instead of firing from a one-shot lifecycle hook: the user, the course
payload and the cookie can each land after the component has mounted.
*/
export function useRegistrationLinkAutoResume({
  course,
  session,
}: {
  course: Ref<CoursePageView>;
  session: Ref<CourseSessionView | undefined>;
}) {
  const { $analytics } = useNuxtApp();
  const flow = useRegistrationLinkFlow();
  const { isRegisteredToCourseSession } = useLearnerProfile();

  const registration_token = computed(() => course.value?.registration_token);

  const { register } = useRegistrationLink({
    session: () => session.value,
    registration_token: () => registration_token.value,
  });

  const canResume = computed(() => {
    if (!import.meta.client) return false;
    if (!session.value || !registration_token.value) return false;
    if (isRegisteredToCourseSession(session.value.id)) return false;

    return flow.isActive(registration_token.value);
  });

  whenever(canResume, () => register(), { immediate: true, once: true });

  onMounted(() => {
    if (!registration_token.value || canResume.value) return;

    $analytics.track("registration_link_viewed");
  });
}
